import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RotateCcw, SearchIcon } from 'lucide-react';
import { DatePickerInput } from '@/components/common/DatePickerInput';
import { OptionSelect } from '@/components/common/OptionSelect';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectIcon,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { pcmpProjectOptionsQueryOptions } from '../api/pcmpProjects.api';
import {
  PCMP_PROJECT_ACTIVE_FILTER,
  PcmpProjectActiveFilterLabels,
  type PcmpProjectActiveFilter,
} from '../constants/pcmpProject.constants';
import type { usePcmpProjectFilters } from '../hooks/usePcmpProjectFilters';

interface PcmpProjectFilterBarProps {
  controller: ReturnType<typeof usePcmpProjectFilters>;
}

const STATE_ALL_VALUE = 'all';

const STATE_ITEMS: { value: string; label: string }[] = [
  { value: STATE_ALL_VALUE, label: 'All' },
  ...PCMP_PROJECT_ACTIVE_FILTER.map((value) => ({
    value,
    label: PcmpProjectActiveFilterLabels[value],
  })),
];

export function PcmpProjectFilterBar({ controller }: PcmpProjectFilterBarProps) {
  const { filters, hasActiveFilters, isDateRangeInvalid, setDraft, handleApply, handleReset } =
    controller;
  const { data: options, isLoading } = useQuery(pcmpProjectOptionsQueryOptions());

  const editionOptions = useMemo(
    () => options?.bookEditions.map((edition) => ({ id: edition.no, name: edition.name })),
    [options],
  );

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (isDateRangeInvalid) return;
        handleApply();
      }}
      className="flex flex-col gap-3 rounded-xl border bg-card p-4 shadow-sm"
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
        <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pcmp-project-edition">Book Edition</Label>
            <OptionSelect
              id="pcmp-project-edition"
              options={editionOptions}
              isLoading={isLoading}
              value={filters.bookEditionNo}
              onChange={(value) => setDraft('bookEditionNo', value)}
              placeholder="All"
              className="h-10"
              clearable
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pcmp-project-company">Company</Label>
            <OptionSelect
              id="pcmp-project-company"
              options={options?.companies}
              isLoading={isLoading}
              value={filters.companyId}
              onChange={(value) => setDraft('companyId', value)}
              placeholder="All"
              className="h-10"
              clearable
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pcmp-project-state">Status</Label>
            <Select<string>
              items={STATE_ITEMS}
              value={filters.activeState ?? STATE_ALL_VALUE}
              onValueChange={(next) =>
                setDraft(
                  'activeState',
                  next === STATE_ALL_VALUE ? undefined : (next as PcmpProjectActiveFilter),
                )
              }
            >
              <SelectTrigger id="pcmp-project-state" className="h-10">
                <SelectValue />
                <SelectIcon />
              </SelectTrigger>
              <SelectContent>
                {STATE_ITEMS.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pcmp-project-from">Active From (on or after)</Label>
            <DatePickerInput
              id="pcmp-project-from"
              value={filters.activeFrom}
              onChange={(value) => setDraft('activeFrom', value)}
              aria-invalid={isDateRangeInvalid}
              className="sm:h-10"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pcmp-project-to">Active To (on or before)</Label>
            <DatePickerInput
              id="pcmp-project-to"
              value={filters.activeTo}
              onChange={(value) => setDraft('activeTo', value)}
              aria-invalid={isDateRangeInvalid}
              className="sm:h-10"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button type="submit" disabled={isDateRangeInvalid} className="h-10 gap-1.5 shadow-sm">
            <SearchIcon className="size-4" />
            Apply
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            disabled={!hasActiveFilters}
            className="h-10 gap-1.5 shadow-sm"
          >
            <RotateCcw className="size-4" />
            Reset
          </Button>
        </div>
      </div>
      {isDateRangeInvalid ? (
        <p role="alert" className="text-xs text-destructive">
          Active To must be on or after Active From.
        </p>
      ) : null}
    </form>
  );
}
